import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CameraAlt from '@mui/icons-material/CameraAlt';
import Refresh from '@mui/icons-material/Refresh';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
} from 'recharts';
import { toast } from 'react-toastify';
import { computeBreezePortfolioKpi } from '../../../utils/breezePortfolioKpi';
import { postBreezeKpiSnapshot, getBreezeKpiSnapshots } from '../../../services/request/BreezeWealth';
import { formatCurrency } from '../../../utils/CurrencyFormat';

export interface KpiSnapshotRow {
  id?: string;
  capturedAt: string;
  stockCount: number;
  largeCapPct: number;
  midCapPct: number;
  smallCapPct: number;
  unclassifiedCapPct?: number;
  investedAmount: number;
  currentValue: number;
  profitLoss: number;
  profitLossPct?: number | null;
}

function startUtcDay(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00.000Z`).toISOString();
}

function endUtcDay(isoDate: string): string {
  return new Date(`${isoDate}T23:59:59.999Z`).toISOString();
}

function breezeKpiErr(e: unknown): string {
  const ax = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: unknown } }).response : undefined;
  const d = ax?.data;
  if (typeof d === 'object' && d !== null && 'error' in d) {
    const er = (d as { error?: unknown }).error;
    if (typeof er === 'string') return er;
  }
  return '';
}

type ChartMode = 'all' | 'amounts' | 'caps' | 'meta';

const BreezePortfolioKpiPanel: React.FC<{ holdingsPl: Record<string, unknown> | null }> = ({ holdingsPl }) => {
  const rows = useMemo(() => {
    const r = holdingsPl?.rows;
    if (!Array.isArray(r)) return [];
    return r as Array<Record<string, unknown>>;
  }, [holdingsPl]);

  const computed = useMemo(() => {
    const inputs = rows.map((row) => ({
      stockCode: String(row.stockCode ?? ''),
      investedAmount: Number(row.investedAmount ?? 0),
      currentAmount: Number(row.currentAmount ?? 0),
      pnlAmount: Number(row.pnlAmount ?? 0),
    }));
    return computeBreezePortfolioKpi(inputs);
  }, [rows]);

  const [chartFrom, setChartFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [chartTo, setChartTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [chartMode, setChartMode] = useState<ChartMode>('all');
  const [history, setHistory] = useState<KpiSnapshotRow[]>([]);
  const [loading, setLoading] = useState(false);

  const chartData = useMemo(() => {
    const fromMs = new Date(startUtcDay(chartFrom)).getTime();
    const toMs = new Date(endUtcDay(chartTo)).getTime();
    return history
      .filter((h) => {
        const t = new Date(h.capturedAt).getTime();
        return t >= fromMs && t <= toMs;
      })
      .map((h) => ({
        label: new Date(h.capturedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        stockCount: h.stockCount,
        largeCapPct: h.largeCapPct,
        midCapPct: h.midCapPct,
        smallCapPct: h.smallCapPct,
        unclassifiedCapPct: h.unclassifiedCapPct ?? 0,
        investedAmount: h.investedAmount,
        currentValue: h.currentValue,
        profitLoss: h.profitLoss,
        profitLossPct: h.profitLossPct ?? 0,
      }));
  }, [history, chartFrom, chartTo]);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getBreezeKpiSnapshots(startUtcDay(chartFrom), endUtcDay(chartTo));
      const data = res.data;
      if (Array.isArray(data)) {
        setHistory(data as KpiSnapshotRow[]);
      } else {
        setHistory([]);
      }
    } catch (e: unknown) {
      toast.error(breezeKpiErr(e) || 'Could not load KPI history.');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [chartFrom, chartTo]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const handleCapture = async () => {
    if (rows.length === 0) {
      toast.error('Load holdings P/L first so KPIs can be computed in the browser.');
      return;
    }
    try {
      setLoading(true);
      await postBreezeKpiSnapshot({
        capturedAt: new Date().toISOString(),
        stockCount: computed.stockCount,
        largeCapPct: computed.largeCapPct,
        midCapPct: computed.midCapPct,
        smallCapPct: computed.smallCapPct,
        unclassifiedCapPct: computed.unclassifiedCapPct,
        investedAmount: computed.investedAmount,
        currentValue: computed.currentValue,
        profitLoss: computed.profitLoss,
        profitLossPct: computed.profitLossPct,
      });
      toast.success('KPI snapshot saved.');
      await loadHistory();
    } catch (e: unknown) {
      toast.error(breezeKpiErr(e) || 'Save failed. Are you logged in to admin?');
    } finally {
      setLoading(false);
    }
  };

  const amountsChart = (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tickFormatter={(v) => formatCurrency(Number(v))} width={72} />
        <ReTooltip formatter={(v: number) => formatCurrency(v)} />
        <Legend />
        <Line type="monotone" dataKey="investedAmount" name="Invested" stroke="#546e7a" dot strokeWidth={2} />
        <Line type="monotone" dataKey="currentValue" name="Current" stroke="#1565c0" dot strokeWidth={2} />
        <Line type="monotone" dataKey="profitLoss" name="P/L" stroke="#2e7d32" dot strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );

  const capsChart = (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <ReTooltip formatter={(v: number) => `${Number(v).toFixed(1)}%`} />
        <Legend />
        <Line type="monotone" dataKey="largeCapPct" name="Large %" stroke="#1565c0" dot />
        <Line type="monotone" dataKey="midCapPct" name="Mid %" stroke="#ef6c00" dot />
        <Line type="monotone" dataKey="smallCapPct" name="Small %" stroke="#2e7d32" dot />
        <Line type="monotone" dataKey="unclassifiedCapPct" name="Unclassified %" stroke="#9e9e9e" dot />
      </LineChart>
    </ResponsiveContainer>
  );

  const metaChart = (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis yAxisId="left" allowDecimals={false} />
        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
        <ReTooltip />
        <Legend />
        <Line yAxisId="left" type="monotone" dataKey="stockCount" name="Stocks" stroke="#6a1b9a" dot />
        <Line yAxisId="right" type="monotone" dataKey="profitLossPct" name="P/L %" stroke="#c62828" dot />
      </LineChart>
    </ResponsiveContainer>
  );

  const singleChart =
    chartMode === 'amounts' ? (
      amountsChart
    ) : chartMode === 'caps' ? (
      capsChart
    ) : chartMode === 'meta' ? (
      metaChart
    ) : (
      <Stack spacing={2} sx={{ width: '100%' }}>
        {amountsChart}
        {capsChart}
        {metaChart}
      </Stack>
    );

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Portfolio KPI (from loaded holdings)
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          KPIs are calculated in your browser from the P/L table below. Cap mix uses a static symbol map (Large / Mid / Small) — refine
          <code> nseCapTier.ts </code>
          over time. Snapshots are stored when you click Capture (no extra ICICI call).
        </Alert>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Stocks
            </Typography>
            <Typography variant="h6">{computed.stockCount}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Invested
            </Typography>
            <Typography variant="h6">{formatCurrency(computed.investedAmount)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Current
            </Typography>
            <Typography variant="h6">{formatCurrency(computed.currentValue)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              P/L
            </Typography>
            <Typography variant="h6" color={computed.profitLoss >= 0 ? 'success.main' : 'error.main'}>
              {formatCurrency(computed.profitLoss)}
              {computed.profitLossPct != null ? ` (${computed.profitLossPct.toFixed(2)}%)` : ''}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Cap mix (by current value)
            </Typography>
            <Typography variant="body2">
              L {computed.largeCapPct}% · M {computed.midCapPct}% · S {computed.smallCapPct}% · ? {computed.unclassifiedCapPct}%
            </Typography>
          </Paper>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
          <Button variant="contained" startIcon={<CameraAlt />} onClick={() => void handleCapture()} disabled={loading || rows.length === 0}>
            Capture snapshot
          </Button>
          <TextField
            label="Chart from"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={chartFrom}
            onChange={(e) => setChartFrom(e.target.value)}
          />
          <TextField
            label="Chart to"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={chartTo}
            onChange={(e) => setChartTo(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Chart series</InputLabel>
            <Select label="Chart series" value={chartMode} onChange={(e) => setChartMode(e.target.value as ChartMode)}>
              <MenuItem value="all">All (3 panels)</MenuItem>
              <MenuItem value="amounts">Invested / Current / P/L only</MenuItem>
              <MenuItem value="caps">Large / Mid / Small / Unclassified %</MenuItem>
              <MenuItem value="meta">Stock count and P/L %</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<Refresh />} variant="outlined" onClick={() => void loadHistory()} disabled={loading}>
            Refresh chart
          </Button>
        </Stack>

        {chartData.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No snapshots in this date range — capture one above.
          </Typography>
        ) : (
          <Box sx={{ width: '100%' }}>{singleChart}</Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BreezePortfolioKpiPanel;
